(() => {
  if (customElements.get('krustic-ensemble-chooser')) return;
  class EnsembleChooser extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.lifecycle = new AbortController();
      this.data = JSON.parse(this.querySelector('[data-keu-json]').textContent);
      this.form = document.getElementById(this.dataset.form);
      this.info = this.closest('.product__info-container');
      if (!this.form || !this.info) return;
      this.single = this.data.singles.find(v => String(v.id) === String(this.data.initialId));
      this.mode = 'single';
      this.radios = [...this.querySelectorAll('input[type=radio]')];
      this.radios[0].checked = true;
      this.radios[1].checked = false;
      this.price = document.getElementById(`price-${this.dataset.section}`);
      if (this.price) {
        this.priceObserver = new MutationObserver(() => this.updateNativePrice());
        this.priceObserver.observe(this.price, {childList:true,subtree:true,characterData:true});
      }
      const refreshAffirm = () => this.updateAffirm();
      document.addEventListener('load', refreshAffirm, {capture:true,signal:this.lifecycle.signal});
      window.addEventListener('load', refreshAffirm, {signal:this.lifecycle.signal});
      this.sticky = document.getElementById('md-sticky-atc');
      this.stickyForm = this.sticky?.querySelector('form[action*="/cart/add"]');
      if (this.sticky) {
        const syncStickyAccessibility = () => this.sticky.setAttribute('aria-hidden', String(!this.sticky.classList.contains('show')));
        this.stickyObserver = new MutationObserver(syncStickyAccessibility);
        this.stickyObserver.observe(this.sticky, {attributes:true,attributeFilter:['class']});
        syncStickyAccessibility();
      }
      this.stickyTitle = this.sticky?.querySelector('.product-content .product__title, .product-content h3, .product-content h2');
      this.originalStickyTitle = this.stickyTitle?.textContent;
      this.originalStickyImage = this.sticky?.querySelector('.product-content img')?.getAttribute('src');
      this.addEventListener('change', event => {
        if (!this.radios.includes(event.target)) return;
        this.mode = event.target.value;
        this.refresh();
      }, {signal:this.lifecycle.signal});
      this.info.addEventListener('change', event => {
        if (event.target.name !== 'id' || !this.form.contains(event.target)) return;
        const single = this.data.singles.find(v => String(v.id) === event.target.value);
        if (single) { this.single = single; this.refresh(); }
      }, {signal:this.lifecycle.signal});
      this.bindForm(this.form);
      if (this.stickyForm) this.bindForm(this.stickyForm);
      this.buttonObserver = new MutationObserver(() => this.updatePurchase());
      this.buttonObserver.observe(this.form.querySelector('[name=add]'), {childList:true,subtree:true});
      this.hidden = false;
      this.refresh();
    }
    disconnectedCallback() {
      this.buttonObserver?.disconnect();
      this.stickyObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.lifecycle?.abort();

      this.ready = false;
    }
    normalize(value) { return String(value || '').trim().toLowerCase(); }
    matched() {
      if (!this.single) return null;
      const matches = this.data.ensembles.filter(v => v.options.length === this.single.options.length && v.options.every((o,i) => this.normalize(o) === this.normalize(this.single.options[i])));
      return matches.length === 1 ? matches[0] : null;
    }
    chosen() { return this.mode === 'ensemble' ? this.matched() : this.single; }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:this.data.currency}).format(cents / 100);
    }
    text(selector, value) {
      const el = this.querySelector(selector);
      if (el && el.textContent !== value) el.textContent = value;
    }
    refresh() {
      const bundle = this.matched();
      const usable = Boolean(bundle?.available && bundle.image);
      this.text('[data-single-price]', this.single?.money || '');
      this.text('[data-ensemble-price]', bundle?.money || '—');
      this.text('[data-difference]', bundle && this.single ? `${bundle.price >= this.single.price ? '+' : '−'}${this.money(Math.abs(bundle.price - this.single.price))}` : '—');
      const image = this.querySelector('[data-ensemble-image]');
      image.hidden = !bundle?.image;
      if (bundle?.image) {
        if (image.getAttribute('src') !== bundle.image) image.src = bundle.image;
        image.alt = `${this.data.ensembleTitle} — ${this.single.options.join(' / ')}`;
      } else image.removeAttribute('src');
      this.radios[1].disabled = !usable;
      const status = this.querySelector('[data-status]');
      status.hidden = usable;
      status.textContent = usable ? '' : this.data.strings.unavailable;
      for (const form of [this.form, this.stickyForm].filter(Boolean)) {
        const button = form.querySelector('[name=add]');
        if (button && !button.classList.contains('loading')) {
          button.removeAttribute('aria-disabled');
          const component = form.closest('product-form');
          if (component) { component.error = false; component.handleErrorMessage?.(); }
        }
      }
      this.updatePurchase();
    }
    setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }
    bindForm(form) {
      form.addEventListener('submit', event => {
        this.updatePurchase();
        const chosen = this.chosen();
        const quantity = form.querySelector('[name=quantity]');
        if (!chosen?.available || (this.mode === 'ensemble' && !chosen.image) || (quantity && !quantity.checkValidity())) {
          event.preventDefault();
          event.stopImmediatePropagation();
          quantity?.reportValidity();
        }
      }, {capture:true,signal:this.lifecycle.signal});
      form.addEventListener('formdata', event => {
        const chosen = this.chosen();
        if (chosen) event.formData.set('id', String(chosen.id));
      }, {signal:this.lifecycle.signal});
    }
    updateNativePrice() {
      const chosen = this.chosen();
      if (!this.price || !chosen) return;
      this.priceObserver?.disconnect();
      this.price.classList.remove('keu-native-price-hidden');
      const frame = this.price.querySelector('.price');
      if (frame) frame.classList.toggle('price--on-sale', !chosen.hideCompare && chosen.compare > chosen.price);
      this.price.querySelectorAll('.price__regular .price-item--regular,.price__sale .price-item--sale').forEach(el => this.setText(el, chosen.money));
      this.price.querySelectorAll('.price__compare .price-item').forEach(el => this.setText(el, chosen.compareMoney || ''));
      this.priceObserver?.observe(this.price, {childList:true,subtree:true,characterData:true});
    }
    updateAffirm() {
      const node = this.info?.querySelector('.affirm-as-low-as');
      const chosen = this.chosen();
      if (!node || !chosen) return;
      node.classList.remove('keu-checkout-hidden');
      const amount = String(chosen.price);
      const changed = node.getAttribute('data-amount') !== amount;
      node.setAttribute('data-amount', amount);
      const ui = window.affirm?.ui;
      if (!ui || typeof ui.ready !== 'function' || typeof ui.refresh !== 'function') return;
      if (this.affirmQueued || (!changed && this.affirmAmount === amount)) return;
      this.affirmQueued = true;
      ui.ready(() => {
        this.affirmQueued = false;
        if (!this.isConnected) return;
        const current = this.chosen();
        if (!current) return;
        node.setAttribute('data-amount', String(current.price));
        this.affirmAmount = String(current.price);
        ui.refresh();
      });
    }
    updatePurchase() {
      const chosen = this.chosen();
      const ensemble = this.mode === 'ensemble';
      const available = Boolean(chosen?.available && (!ensemble || chosen.image));
      const label = available ? (ensemble ? this.data.strings.ensembleCta : this.data.strings.singleCta) : this.data.strings.soldOut;
      for (const form of [this.form, this.stickyForm].filter(Boolean)) {
        const input = form.querySelector('[name=id]');
        if (input && !chosen) input.disabled = true;
        if (input && chosen) {
          if (input.tagName === 'SELECT' && ![...input.options].some(o => o.value === String(chosen.id))) {
            const option = new Option(this.data.ensembleTitle, String(chosen.id));
            option.dataset.keu = 'true';
            input.add(option);
          }
          input.value = String(chosen.id);
          input.disabled = false;
        }
        const button = form.querySelector('[name=add]');
        if (button && !button.classList.contains('loading') && button.getAttribute('aria-disabled') !== 'true') {
          button.disabled = !available;
          const target = button.querySelector('.button__label') || button.querySelector('span');
          this.setText(target, label);
          if (target?.hasAttribute('data-label')) target.setAttribute('data-label', label);
        }
        const qty = form.querySelector('[name=quantity]');
        if (qty && chosen) {
          qty.min = chosen.min || 1;
          qty.step = chosen.step || 1;
          if (chosen.max != null) qty.max = chosen.max; else qty.removeAttribute('max');
        }
      }
      this.form.querySelectorAll('.product-form__checkout,.shopify-payment-button').forEach(el => el.classList.toggle('keu-checkout-hidden', ensemble));
      this.updateNativePrice();
      this.updateAffirm();
      if (this.sticky && chosen) {
        this.setText(this.sticky.querySelector('[data-sticky-price]'), chosen.money);
        const compare = this.sticky.querySelector('[data-sticky-compare]');
        if (compare) compare.hidden = ensemble;
        const image = this.sticky.querySelector('.product-content img');
        if (image && ensemble && chosen.image) image.src = chosen.image;
        if (image && !ensemble) {
          const select = this.stickyForm?.querySelector('select[name=id]');
          const source = select?.selectedOptions[0]?.dataset.img;
          if (source) image.src = source;
        }
        this.setText(this.sticky.querySelector('[data-sticky-variant-label]'), this.single?.options.join(' / ') || '');
        if (this.stickyTitle) this.setText(this.stickyTitle, ensemble ? this.data.ensembleTitle : this.originalStickyTitle);
      }
    }
  }
  customElements.define('krustic-ensemble-chooser', EnsembleChooser);
})();
